import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Ref } from "react";
import {
  Box,
  Checkbox,
  FormHelperText,
  Paper,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { theme } from "../theme/theme";

export interface CompanyOption {
  id: string;
  cuit: string;
  comercialName: string;
  legalName: string;
}
interface Props {
  options: CompanyOption[];
  resultIds?: string[];
  value: string[];
  onChange: (ids: string[]) => void;
  onBlur?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  total: number;
  onRequest: (search: string, page: number, rowPerPage: number) => void;
}
export default function CompanyMultiSelect({
  options,
  resultIds,
  value,
  onChange,
  onBlur,
  inputRef,
  error,
  disabled = false,
  loading = false,
  total,
  onRequest,
}: Props) {
  const id = useId();
  const [search, setSearch] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [request, setRequest] = useState({ search: "", page: 0, size: 20 });
  const callback = useRef(onRequest);
  callback.current = onRequest;
  const theme = useTheme();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setRequest((previous) =>
        previous.search === search.trim()
          ? previous
          : { ...previous, search: search.trim(), page: 0 },
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Mount, search, page and page-size changes all request the corresponding API page.
  useEffect(() => {
    if (!onlySelected)
      callback.current(request.search, request.page, request.size);
  }, [request, onlySelected]);

  const selected = useMemo(() => new Set(value), [value]);
  const rows = useMemo(() => {
    const byId = new Map(options.map((option) => [option.id, option]));
    if (onlySelected)
      return value.map(
        (key) =>
          byId.get(key) ?? {
            id: key,
            cuit: "",
            comercialName: `Cliente (${key})`,
            legalName: "Datos no disponibles",
          },
      );
    return (resultIds ?? []).flatMap((key) => {
      const option = byId.get(key);
      return option ? [option] : [];
    });
  }, [options, resultIds, onlySelected, value]);
  const count = onlySelected ? rows.length : total;
  const currentPage = onlySelected
    ? Math.min(request.page, Math.max(0, Math.ceil(count / request.size) - 1))
    : request.page;
  const visible = onlySelected
    ? rows.slice(currentPage * request.size, (currentPage + 1) * request.size)
    : rows;
  const pendingSearch = search.trim() !== request.search;
  const busy = !onlySelected && (loading || pendingSearch);
  const toggle = (key: string) => {
    if (disabled || busy) return;
    onChange(
      selected.has(key)
        ? value.filter((item) => item !== key)
        : [...value, key],
    );
  };
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, minWidth: 0, borderColor: error ? "error.main" : "divider" }}
    >
      <Typography id={`${id}-label`} variant="subtitle1">
        Empresas *
      </Typography>
      <TextField
        fullWidth
        size="small"
        label="Buscar por CUIT, nombre o razón social"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onBlur={onBlur}
        inputRef={inputRef}
        disabled={disabled || onlySelected}
        sx={{ mt: 1 }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "space-between",
          my: 1,
        }}
      >
        <Typography variant="body2" aria-live="polite">
          {value.length} empresas seleccionadas ·{" "}
          {busy ? "Cargando resultados…" : `${count} resultados disponibles`}
        </Typography>
        <FormControlLabel
          label="Ver seleccionadas"
          control={
            <Switch
              size="small"
              checked={onlySelected}
              disabled={disabled}
              onChange={(_, checked) => {
                setOnlySelected(checked);
                setRequest((previous) => ({ ...previous, page: 0 }));
              }}
            />
          }
        />
      </Box>
      <TableContainer
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 119, 194, 0.1)",
          border: `1px solid ${theme.palette.primary.light}20`,
          overflow: "auto",
          maxWidth: "100%",
          width: "100%",
          maxHeight: 440,
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-labelledby={`${id}-label`}
          sx={{ tableLayout: "fixed", minWidth: 740 }}
        >
          <colgroup>
            <col style={{ width: 48 }} />
            <col style={{ width: 145 }} />
            <col style={{ width: 240 }} />
            <col />
          </colgroup>
          <TableHead
            sx={{
              "& .MuiTableCell-head": {
                backgroundColor: "primary.main",
                color: "common.white",
                fontWeight: 600,
                borderBottom: "none",
              },
            }}
          >
            <TableRow>
              <TableCell padding="checkbox" aria-label="Selección" />
              <TableCell>CUIT</TableCell>
              <TableCell>Nombre comercial</TableCell>
              <TableCell>Razón social</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((option) => (
              <TableRow
                key={option.id}
                hover
                selected={selected.has(option.id)}
                onClick={() => toggle(option.id)}
                sx={{ cursor: disabled || busy ? "default" : "pointer" }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.has(option.id)}
                    disabled={disabled || busy}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggle(option.id)}
                    onBlur={onBlur}
                    slotProps={{
                      input: {
                        "aria-label": `Seleccionar ${option.comercialName}, CUIT ${option.cuit}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
                >
                  {option.cuit || "—"}
                </TableCell>
                <TableCell sx={{ overflowWrap: "anywhere" }}>
                  {option.comercialName || "—"}
                </TableCell>
                <TableCell sx={{ overflowWrap: "anywhere" }}>
                  {option.legalName || "—"}
                </TableCell>
              </TableRow>
            ))}
            {!visible.length && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  {loading
                    ? "Cargando empresas…"
                    : "No hay empresas para mostrar."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={currentPage}
        rowsPerPage={request.size}
        disabled={disabled || busy}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(_, next) =>
          setRequest((previous) => ({ ...previous, page: next }))
        }
        onRowsPerPageChange={(event) =>
          setRequest((previous) => ({
            ...previous,
            size: Number(event.target.value),
            page: 0,
          }))
        }
        labelRowsPerPage="Por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} de ${count}`
        }
        getItemAriaLabel={(type) =>
          type === "next"
            ? "Página siguiente"
            : type === "previous"
              ? "Página anterior"
              : type === "first"
                ? "Primera página"
                : "Última página"
        }
        sx={{
          "& .MuiTablePagination-toolbar": { flexWrap: "wrap", pl: 0 },
          "& .MuiTablePagination-spacer": { display: "none" },
        }}
      />
      {error && (
        <FormHelperText error role="alert">
          {error}
        </FormHelperText>
      )}
    </Paper>
  );
}
